import React, { memo, FC, useState, useEffect, useContext } from "react"
import LoginContainer from "../../views/LoginContainer/LoginContainer"
import css from "./CompletePasswordLessFlowPanel.module.css"
import { URL_LOGIN, URL_LOGIN_EMAIL } from "../../models/url"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import { Button, Form, Input, Space, Spin, message } from "antd"
import { useCompletePasswordlessLoginEndUserAppMutation } from "../../generated/graphql"
import { MainContext } from "../../layouts/Main/MainLayout"
import { useHistory, useLocation } from "react-router"
import { margin } from "../../styles/layout"
import * as Sentry from "@sentry/react"
import { blue, grey } from "@ant-design/colors"
import ErrorPanel, { KnownError } from "../../errors/ErrorPanel"
import { ParentParams } from "../../app/App"
import { LocationState } from "../../types"
import { useTranslation } from "react-i18next"
import { safeStorage } from "../../helpers/safeStorage"
import { postMessageToParent } from "../../helpers/safePostMessage"
import { clearPersistedLoginFromUrl, resolveLoginFromUrl } from "./loginFromUrl"

type CompletePasswordLessFlowPanelProps = {
    setToken: (token: string) => void
    email?: string
    parentUrl: string
    paramCode?: string
    passwordlessIdCodeLength: number
    setParentParams: (params?: ParentParams) => void
}

type Digit = "" | "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
type CodeType = {
    [key in `digit${number}`]: Digit | undefined
}

const CompletePasswordLessFlowPanel: FC<CompletePasswordLessFlowPanelProps> = ({ setToken, email, parentUrl, paramCode, passwordlessIdCodeLength, setParentParams }) => {
    const history = useHistory()
    const location = useLocation<LocationState>()
    const { userAuthDate } = useContext(MainContext)
    const { t } = useTranslation()
    const initializeCodeState = (length: number) => {
        let initialState: CodeType = {}
        for (let i = 1; i <= length; i++) {
            initialState[`digit${i}`] = undefined
        }
        return initialState
    }
    const [code, setCode] = useState<CodeType>(initializeCodeState(passwordlessIdCodeLength))
    const noCodeDigitIsUndefined = Object.values(code).every((digit) => digit !== undefined)
    const [error, setError] = useState<string | undefined>()

    // completePasswordlessFlowEndUserApp
    const [completePasswordlessFlow, { data, loading: completePasswordlessLoading }] = useCompletePasswordlessLoginEndUserAppMutation()

    const completePasswordlessData = data?.completePasswordlessFlowEndUserApp
    // Beim Magic-Link öffnet der E-Mail-Link eine NEUE Navigation — der Location-State
    // ist dann weg. Deshalb fällt die Auflösung auf die beim Login-Start persistierte
    // fromUrl zurück (siehe loginFromUrl.ts), bevor URL_BASE greift.
    const fromUrl = resolveLoginFromUrl(location.state?.fromUrl)

    const removeParametersFromParent = () => {
        postMessageToParent("removeParameters")
        setParentParams()
    }

    useEffect(() => {
        if (completePasswordlessData) {
            const { accessToken, refreshToken, booker, id } = completePasswordlessData
            if (accessToken && refreshToken && booker && id) {
                setToken(accessToken)
                safeStorage.setItem("accessToken", accessToken)
                safeStorage.setItem("refreshToken", refreshToken)
                safeStorage.setItem("booker", JSON.stringify(booker))
                safeStorage.setItem("loggedInUser", id)
                // Dispatch custom event für localStorage-Änderungen im App.tsx
                window.dispatchEvent(new Event('localStorageChange'))
                if (userAuthDate) {
                    userAuthDate(new Date())
                }
                removeParametersFromParent()
                // Persistierte fromUrl ist verbraucht — entfernen
                clearPersistedLoginFromUrl()
                history.push(fromUrl)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completePasswordlessData])

    const backToLoginUrl = email ? URL_LOGIN_EMAIL.replace(":email", email) : URL_LOGIN

    const backToLoginButton = (
        <Button
            type='primary'
            onClick={() => {
                removeParametersFromParent()
                history.push(backToLoginUrl)
            }}
        >
            Zurück zum Login
        </Button>
    )

    const knownErrors: Array<KnownError> = [
        {
            key: "Invalid code",
            title: "Falscher Code",
            msg: "Der eingegebene Code ist leider falsch. Bitte versuche es erneut.",
            actionBtn: backToLoginButton,
        },
        {
            // Backend-Vertrag (LoginErrorMessages.NO_CUSTOMER_ACCOUNT): Code korrekt, aber zur E-Mail gibt es
            // kein Kundenkonto (mehr): Bucher gelöscht oder Mitarbeiter-Zugang. Die Ursache kennt der Client
            // nicht, daher ein neutraler Text statt des Mitarbeiter-Hinweises aus dem Passwort-Login.
            key: "noCustomerAccount",
            title: t("login.errors.noCustomerAccount.title", "Kein Kundenkonto für diese E-Mail-Adresse"),
            msg: t(
                "completePasswordLessFlowPanel.noCustomerAccountMsg",
                "Zu dieser E-Mail-Adresse gibt es kein Kundenkonto. Bitte wende dich an den Anbieter: Er kann dich in der Verwaltung als Kunde anlegen."
            ),
            actionBtn: backToLoginButton,
        },
    ]

    const handleSubmit = async () => {
        const loginCode = Object.values(code).join("")

        const isLoggedIn = safeStorage.getItem("loggedInUser")

        if (isLoggedIn) {
            removeParametersFromParent()
            clearPersistedLoginFromUrl()
            history.push(fromUrl)
            // Bereits eingeloggt → Redirect genügt. Ohne return würde die Mutation trotzdem
            // feuern (unnötiger Request, möglicher Fehler-Overlay nach der Navigation).
            return
        }

        completePasswordlessFlow({ variables: { loginCode } })
            .then((res) => {
                const booker = res.data?.completePasswordlessFlowEndUserApp?.booker
                if (res.data?.completePasswordlessFlowEndUserApp?.accessToken) {
                    message.success(`Hi ${booker?.firstname} ${booker?.lastname} du wurdest erfolgreich eingeloggt`)
                    removeParametersFromParent()
                    clearPersistedLoginFromUrl()
                    history.push(fromUrl)
                }
            })
            .catch((error: any) => {
                console.log("error:", error)
                const errorStr = JSON.stringify(error)
                if (errorStr.includes("statusCode: 404")) {
                    setError("404")
                } else if (errorStr.includes("Invalid code")) {
                    setError("Invalid code")
                } else if (errorStr.includes("No customer account")) {
                    setError("noCustomerAccount")
                } else {
                    Sentry.captureException(error)
                }
            })
    }

    useEffect(() => {
        if (paramCode && passwordlessIdCodeLength) {
            if (!noCodeDigitIsUndefined) {
                let updatedCode: CodeType = { ...code }
                for (let i = 0; i < passwordlessIdCodeLength && i < paramCode.length; i++) {
                    updatedCode[`digit${i + 1}`] = paramCode[i] as Digit
                }
                setCode(updatedCode)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramCode, passwordlessIdCodeLength])

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const pastedData = event.clipboardData.getData("text")
        const digits = pastedData.split("")

        let updatedCode: CodeType = { ...code }
        for (let i = 0; i < passwordlessIdCodeLength && i < digits.length; i++) {
            updatedCode[`digit${i + 1}`] = digits[i] as Digit
        }

        form.setFieldsValue({
            code: updatedCode,
        })

        setCode(updatedCode)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { name, value } = event.target
        const updatedCode = { ...code, [name]: value }
        setCode(updatedCode)

        // If a value is entered, focus the next input field
        if (value && index < passwordlessIdCodeLength - 1) {
            const nextIndex = index + 1
            const nextInput = document.querySelector(`input[data-index='${nextIndex}']`) as HTMLInputElement
            nextInput && nextInput.focus()
        }
    }

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.select()
    }

    const description1 = email
        ? t("completePasswordLessFlowPanel.description1a", "Wir haben dir einen Bestätigungscode an {{email}} gesendet.", { email })
        : t("completePasswordLessFlowPanel.description1b", "Wir haben dir einen Bestätigungscode per Email an die eingetragene Adresse gesendet.")
    const description2 = t("completePasswordLessFlowPanel.description2", "Bitte gib diesen Code hier ein.")

    const [form] = Form.useForm()
    const renderContent = () => {
        if (completePasswordlessLoading) {
            return (
                <Space direction='vertical' size='large'>
                    <h3 style={{ color: grey[3] }}>Einen Moment bitte</h3>
                    <Spin />
                    <h2 style={{ color: grey[4], marginBottom: margin.std, fontWeight: 600 }}>Wir loggen dich ein...</h2>
                </Space>
            )
        }

        if (error) {
            return <ErrorPanel error={error} knownErrors={knownErrors} />
        }

        const devidableBy = () => {
            if (passwordlessIdCodeLength % 5 === 0) {
                return 5
            } else if (passwordlessIdCodeLength % 4 === 0) {
                return 4
            } else if (passwordlessIdCodeLength % 3 === 0) {
                return 3
            } else if (passwordlessIdCodeLength % 2 === 0) {
                return 2
            } else {
                return 0
            }
        }

        return (
            <div className={css.root}>
                <LayoutForm onSubmitCapture={handleSubmit} columns={1} form={form}>
                    <Space direction='vertical' size='middle'>
                        <p className={css.description}>{description1}</p>
                    </Space>
                    <Form.Item>
                        <Input.Group className={css.inputGroup} style={{ marginBottom: margin.std }}>
                            {/* take codeLenght and generate InputFields */}
                            {Array.from(Array(passwordlessIdCodeLength).keys()).map((index) => {
                                return (
                                    <Form.Item name={["code", `digit${index + 1}`]} noStyle key={index} label='?'>
                                        <span style={{ display: "none" }}>{code[`digit${index + 1}`]}</span>
                                        <Input
                                            name={`digit${index + 1}`}
                                            data-index={index}
                                            style={{
                                                width: "32px",
                                                marginLeft: index % devidableBy() === 0 ? 48 / passwordlessIdCodeLength : 12 / passwordlessIdCodeLength,
                                                marginRight: (index + 1) % devidableBy() === 0 ? 48 / passwordlessIdCodeLength : 12 / passwordlessIdCodeLength,
                                                height: "42px",
                                                borderRadius: "5px",
                                                border: `2px solid ${blue[2]}`,
                                                textAlign: "center",
                                                padding: 0,
                                                fontSize: "20px",
                                            }}
                                            value={code[`digit${index + 1}`]}
                                            maxLength={1}
                                            onPaste={handlePaste}
                                            onFocus={handleFocus}
                                            onChange={(event) => handleChange(event, index)}
                                        />
                                    </Form.Item>
                                )
                            })}
                        </Input.Group>
                    </Form.Item>
                    <span className={css.description} style={{ fontWeight: 600 }}>
                        {description2}
                    </span>
                    <Button htmlType='submit' type={noCodeDigitIsUndefined ? "primary" : "default"}>
                        Bestätigen
                    </Button>
                </LayoutForm>
            </div>
        )
    }

    return <LoginContainer title={"Bestätigungscode"} size='xs' style={{ paddingBottom: margin.std }} content={renderContent()} backToUrl={backToLoginUrl} />
}

export default memo(CompletePasswordLessFlowPanel)
